import { Box, Center, HStack } from '@chakra-ui/layout'
import {
  Button,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text as ChakraText,
  useDisclosure,
} from '@chakra-ui/react'
import type { FormikHelpers } from 'formik'
import { useFormik } from 'formik'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useFetch, { CachePolicies } from 'use-http'
import * as Yup from 'yup'
import { colors } from '../../../ui-kit'
import { BackupBucketSettings } from '../../components/Settings'
import type { BackupBucketForm, BackupBucketResponse } from '../../interfaces'
import { UserContext } from '../../providers'
import { MainContentHeadingBox } from '../Base/MainContent'
import PermissionGuard from '../../components/PermissionGuard'
import useToast from '../../hooks/useToast'
import { FeaturesEnum } from '../../../utils/features'
import { PermissionsEnum } from '../../../utils'

const BackupBucketSettingsLayout = (): React.ReactElement => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)

  const { isOpen, onClose, onOpen } = useDisclosure()

  const [newBucket, setNewBucket] = useState<boolean>(true)

  const toast = useToast()

  const { request, loading } = useFetch<BackupBucketResponse>({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const getData = async (): Promise<void> => {
    if (user?.clientNumber) {
      const response = await request.get(
        `/api/clients/${user.clientNumber}/backupBucket`,
      )

      if (!request.error && response) {
        setNewBucket(false)
        const { typeId, bucketName, bucketPrefix, clientNumber, credentials } =
          response
        form.resetForm({
          values: {
            typeId,
            bucketName,
            bucketPrefix,
            clientNumber,
            credentials,
          },
        })
      } else {
        setNewBucket(true)
        form.resetForm({
          values: {
            typeId: 1,
            bucketName: '',
            bucketPrefix: '',
            clientNumber: user.clientNumber,
            credentials: {
              s3AccessKey: '',
              s3SecretKey: '',
            },
          },
        })
      }
    }
  }

  const handleSubmit = async (
    values: BackupBucketForm,
    _formikHelpers: FormikHelpers<BackupBucketForm>,
  ): Promise<void> => {
    if (user?.clientNumber) {
      await request.put(
        `/api/clients/${user.clientNumber}/backupBucket`,
        values,
      )
      setSubmitting(true)
      if (request.error) {
        toast({
          description: t('something_went_wrong', 'Something went wrong!'),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
        return
      }
      toast({
        description: t(
          'successfully_updated_backup_bucket',
          'Successfully updated Backup bucket',
        ),
        status: 'success',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      await getData()
    }
  }

  const handleDelete = async (): Promise<void> => {
    if (user?.clientNumber) {
      await request.delete(`/api/clients/${user.clientNumber}/backupBucket`)
      if (request.error) {
        toast({
          description: t('something_went_wrong', 'Something went wrong!'),
          status: 'error',
          isClosable: true,
          duration: 5000,
          position: 'top-right',
          variant: 'outline',
        })
        return
      }
      toast({
        description: t(
          'successfully_deleted_backup_bucket',
          'Successfully deleted Backup bucket',
        ),
        status: 'success',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      await getData()
    }
  }

  const form = useFormik<BackupBucketForm>({
    initialValues: {
      typeId: 1,
      bucketName: '',
      bucketPrefix: '',
      clientNumber: user?.clientNumber ?? '',
      credentials: {
        s3AccessKey: '',
        s3SecretKey: '',
      },
    },
    validationSchema: Yup.object().shape({
      bucketName: Yup.string()
        .min(1, 'Minimum length should be 1')
        .required(t('required', 'Required')),
      bucketPrefix: Yup.string()
        .min(1, 'Minimum length should be 1')
        .required(t('required', 'Required')),
      credentials: Yup.object().shape({
        s3AccessKey: Yup.string()
          .min(1, 'Minimum length should be 1')
          .required(t('required', 'Required')),
        s3SecretKey: Yup.string()
          .min(1, 'Minimum length should be 1')
          .required(t('required', 'Required')),
      }),
    }),
    onSubmit: handleSubmit,
  })

  const { dirty, submitForm, isSubmitting, setSubmitting, isValid } = form

  useEffect(() => {
    void getData()
  }, [user])

  if (loading) {
    return (
      <Center height="100%">
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor="appPaleGrey"
          color="appBlue"
          size="xl"
        />
      </Center>
    )
  }

  return (
    <PermissionGuard permission={PermissionsEnum.SELF_IMAGE_SETTINGS_MANAGE}>
      {(hasPermission: boolean) => (
        <>
          {user?.pricingPlan.featuresList.includes(
            FeaturesEnum.MEDIA_LIBRARY_BACKUP,
          ) ? (
            <MainContentHeadingBox reverse={true}>
              <HStack spacing="1em">
                <Button
                  variant="deleteCTA"
                  onClick={onOpen}
                  isDisabled={newBucket}
                >
                  {t('delete')}
                </Button>
                <Button
                  onClick={() => {
                    void submitForm()
                  }}
                  isLoading={isSubmitting}
                  isDisabled={!dirty || !isValid}
                >
                  {t('save')}
                </Button>
              </HStack>
            </MainContentHeadingBox>
          ) : (
            <MainContentHeadingBox>
              <Stack direction={['column']} spacing={1}>
                <Heading
                  as="h2"
                  size="md"
                  fontWeight="600"
                  color={colors.appDarkIndigo}
                >
                  {t('backup_bucket', 'Backup Bucket')}
                </Heading>
                <ChakraText color="appBattleshipGrey">
                  {t(
                    'backup_bucket_description',
                    'Backup all media library assets in your S3 bucket by setting up an active backup bucket here',
                  )}
                </ChakraText>
              </Stack>
            </MainContentHeadingBox>
          )}
          <Box padding="1.5em">
            <BackupBucketSettings
              user={user}
              form={form}
              disabled={
                !hasPermission ||
                !user?.pricingPlan.featuresList.includes(
                  FeaturesEnum.MEDIA_LIBRARY_BACKUP,
                )
              }
            />
          </Box>
          <Modal isOpen={isOpen} onClose={onClose}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>
                {t('delete_backup_bucket', 'Delete Backup Bucket')}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <ChakraText>
                  {t(
                    'delete_backup_bucket_description',
                    'This will disable backup.\n This action is irreversible.',
                  )}
                </ChakraText>
              </ModalBody>
              <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
                <Button onClick={onClose} variant="outline">
                  {t('cancel')}
                </Button>
                <Button
                  type="submit"
                  isLoading={form.isSubmitting}
                  backgroundColor={colors.appWatermelon}
                  borderColor={colors.appWatermelon}
                  onClick={async () => {
                    await handleDelete()
                    onClose()
                  }}
                >
                  {t('submit')}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </>
      )}
    </PermissionGuard>
  )
}

export default BackupBucketSettingsLayout
